import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, tap } from "rxjs";
import { translate } from '@jsverse/transloco';
import { AppNotificationService } from "src/app/shared/services/notification.service";
import * as InstallationActions from "../installations/installation-actions";
import { InstallationService } from "src/app/installations/services/installation.service";
import { Router } from "@angular/router";
import { IGetInstallationResponse } from "src/app/installations/models/installation";

@Injectable()
export class InstallationEffets {

    ////////////////////////
    // GET INSTALLATIONS //
    //////////////////////

    // Get Installations
    getInstallations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InstallationActions.getInstallations), 
            mergeMap(() => {
            return this.installationService.searchInstallations({pageNumber: 0, pageSize: 25}).pipe(
                map((response) => 
                    InstallationActions.getInstallationsSuccess({ installations: response.data })
                ), 
                catchError((error) =>
                    of(InstallationActions.getInstallationsFailure({ error: error.message }))
                )
            );
        })
        )
    );


    // Get Installations by company
    getInstallationsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InstallationActions.getInstallationsByCompany), 
            mergeMap((action) => {
            return this.installationService.getInstallationsByCompany(action.request).pipe(
                map((response) => 
                    InstallationActions.getInstallationsByCompanySuccess({ installations: response.data })
                ), 
                catchError((error) =>
                    of(InstallationActions.getInstallationsFailure({ error: error.message }))
                )
            );
        })
        )
    );




    /////////////////////////////
    // GET INSTALLATION BY ID //
    ///////////////////////////

    // Get Installation by id
    getInstallationById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InstallationActions.getInstallationById), 
            mergeMap((action) => {
            return this.installationService.getInstallationById(action.id).pipe(
                map((response) => 
                    InstallationActions.getInstallationByIdSuccess({ installation: response })
                ), 
                catchError((error) =>
                    of(InstallationActions.getInstallationByIdFailure({ error: error.message }))
                )
            );
        })
        )
    );




    ///////////////////////////
    // CREATE INSTALLATION  //
    /////////////////////////

    // Create Installation
    createInstallation$ = createEffect(() =>
      this.actions$.pipe(
          ofType(InstallationActions.createInstallation),
          mergeMap((action) => 
              this.installationService.createInstallation(action.installation).pipe(
                  map((installation: IGetInstallationResponse) => InstallationActions.createInstallationSuccess({ installation })),
                  catchError((error) => of(InstallationActions.createInstallationFailure({ error: error.message })))
              )
          )
      )
    );

    // Installation created successfully
    createInstallationSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(InstallationActions.createInstallationSuccess),
            tap((response) => {
                // Get the current URL
                const currentUrl = this.router.url;

                // Find the position of the first "/"
                const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1))

                // Navigate to the modified URL
                this.router.navigate([baseUrl + "/" + response.installation.id]).then(() => {
                    // Reload the page after navigation
                    window.location.reload();
                });
                this.appnotificationService.notification$.next({ message:translate('installations.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Installation not created successfully
    createInstallationFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(InstallationActions.createInstallationFailure),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('installations.createFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );




    //////////////////////////
    // UPDATE INSTALLATION //
    ////////////////////////

    // Update Installation
    updateInstallation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InstallationActions.updateInstallation),
            mergeMap((action) => 
                this.installationService.updateInstallation(action.installation).pipe(
                    map((installation: IGetInstallationResponse) => InstallationActions.updateInstallationSuccess({ installation })),
                    catchError((error) => of(InstallationActions.updateInstallationFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Installation successful
    updateInstallationSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(InstallationActions.updateInstallationSuccess),
            tap(() => {
                // Reload the page after navigation
                window.location.reload();

                // Success message
                this.appnotificationService.notification$.next({ message:translate('installations.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Installation not updated successfully
    updateInstallationFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(InstallationActions.updateInstallationFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('installations.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    //////////////////////////
    // DELETE INSTALLATION //
    ////////////////////////

    // Delete Installation
    deleteInstallation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InstallationActions.deleteInstallation), 
            mergeMap((action) => {
                return this.installationService.deleteInstallation(action.id).pipe(
                    map(() => InstallationActions.deleteInstallationSuccess({id: action.id})), 
                    catchError((error) => of(InstallationActions.deleteInstallationFailure({ error: error.message })))
            )}
        ))
    );

    // Installation deleted successfully
    deleteInstallationSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(InstallationActions.deleteInstallationSuccess),
          tap(() => {
            this.router.navigate(['/installations-admin']);
            this.appnotificationService.notification$.next({ message:translate('installations.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Installation not deleted successfully
    deleteInstallationFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(InstallationActions.deleteInstallationFailure),
        tap(() => {
            this.appnotificationService.notification$.next({ message:translate('installations.deleteFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );



    constructor(
        private actions$: Actions, 
        private installationService: InstallationService,
        private router: Router,
        private appnotificationService: AppNotificationService) {}
}